import { useContext, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import copyToClipboard from 'copy-to-clipboard';

import { MeContext } from 'auth/AuthContextProvider';
import { HomeSidebar } from 'components/HomeSidebar';
import { Layout } from 'components/Layout';
import { Panel } from 'components/Panel';

import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';

// Couleur (thème de l'app) utilisée pour les modules sombres du QR code.
const C_DARK = '#003440'; // teal.900

const ASSETS = '/img/kit-communication';
const NEUTRAL_URL = 'https://www.monemprunt.com';

const CTAS: [string, string][] = [
  [
    'Capacité d’achat',
    'Connaître votre capacité d’emprunt, c’est cibler les bons biens dès le départ.',
  ],
  [
    'Avant les visites',
    'Avant de visiter, vérifiez que votre budget correspond réellement à votre projet.',
  ],
  ['Mensualité', 'Un bien vous plaît ? Estimez rapidement la mensualité associée.'],
  ['Sérénité', 'Avancer avec les bons chiffres, c’est avancer avec plus de confiance.'],
  ['Rapidité', 'Une première estimation en quelques minutes, sans engagement.'],
  [
    'Accompagnement',
    'Je vous aide à trouver le bon bien. Monemprunt vous aide à poser la base du financement.',
  ],
  ['Projet immobilier', 'Le financement fait partie du projet. Prenez une longueur d’avance.'],
  ['Conversion', 'Vous avez un projet ? Lancez votre simulation et parlons-en ensuite.'],
  ['CTA direct', 'Découvrez votre capacité d’emprunt en quelques minutes.'],
  ['CTA direct', 'Estimez votre budget immobilier avant de vous lancer.'],
  ['CTA direct', 'Faites votre simulation gratuitement.'],
  ['CTA direct', 'Préparez votre projet immobilier avec les bonnes informations.'],
];

// ---- Helpers canvas (bannières) ----
function drawRounded(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function imageLoad(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const i = new window.Image();
    i.onload = () => resolve(i);
    i.onerror = () => resolve(null);
    i.src = src;
  });
}

export const KitCommunicationPage = () => {
  const me = useContext(MeContext);
  const toast = useToast();

  // Lien personnel de l'utilisateur (déduit du contexte, non éditable).
  const lienPersonnel =
    me?.marque?.url_tunnel && me?.utilisateur?.apporteur_code
      ? `${me.marque.url_tunnel}?apcode=${me.utilisateur.apporteur_code}`
      : '';

  const currentLink = /^https?:\/\//i.test(lienPersonnel) ? lienPersonnel : '';
  const visibleLink = currentLink || '[VOTRE LIEN PERSONNALISÉ]';

  const notify = (msg: string) =>
    toast({ title: msg, status: 'success', duration: 1800, isClosable: true });

  const copy = (text: string, msg: string) => {
    // copy-to-clipboard gère le fallback execCommand (fonctionne hors contexte sécurisé/HTTP).
    const ok = copyToClipboard(text);
    if (ok) notify(msg);
    else
      toast({
        title: 'Impossible de copier, veuillez réessayer.',
        status: 'error',
        duration: 2500,
        isClosable: true,
      });
  };

  return (
    <>
      <Center width="100%">
        <Text pl="20px" width="100%" as="h3" fontSize="6xl" fontWeight="semibold" my={6}>
          Kit de communication
        </Text>
      </Center>

      <Box px={{ base: 4, lg: 5 }} pb={10}>
        <Text color="gray.500" fontSize="lg" mb={7}>
          Des contenus prêts à l’emploi pour promouvoir vos simulations. Votre lien personnel est
          automatiquement intégré aux textes et aux QR codes.
        </Text>

        <Tabs variant="line" colorScheme="teal" isLazy={false}>
          <TabList
            overflowX="auto"
            overflowY="hidden"
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            {['Démarrer', 'Vignettes & bannières', 'Accroches & CTA', 'Exemple d’annonce'].map(
              (label) => (
                <Tab key={label} whiteSpace="nowrap" fontWeight="semibold">
                  {label}
                </Tab>
              ),
            )}
          </TabList>

          <TabPanels bg="transparent">
            <TabPanel pt={8}>
              <StartPanel visibleLink={visibleLink} hasLink={!!currentLink} />
            </TabPanel>
            <TabPanel pt={8}>
              <BannersPanel currentLink={currentLink} notify={notify} />
            </TabPanel>
            <TabPanel pt={8}>
              <CtaPanel visibleLink={visibleLink} copy={copy} />
            </TabPanel>
            <TabPanel pt={8}>
              <AnnouncesPanel visibleLink={visibleLink} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};

// ---- Briques UI communes ----
// Interaction de survol partagée par les cartes (lift + ombre teintée teal).
const cardHover = {
  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
  _hover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 28px rgba(0, 160, 162, 0.16)',
  },
};

// Signature : petite barre d'accent teal devant chaque eyebrow.
const Kicker = ({ children }: { children: React.ReactNode }) => (
  <HStack spacing={3} mb={3} align="center">
    <Box w="26px" h="3px" bg="teal.500" borderRadius="full" flexShrink={0} />
    <Text
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="1.6px"
      color="teal.500"
      textTransform="uppercase"
    >
      {children}
    </Text>
  </HStack>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Heading
    as="h2"
    fontSize={{ base: '5xl', md: '6xl' }}
    fontWeight="bold"
    color="teal.900"
    lineHeight="shorter"
    letterSpacing="-0.01em"
  >
    {children}
  </Heading>
);

const Lede = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" color="gray.500" mt={2}>
    {children}
  </Text>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <Box
    mt={7}
    px={6}
    py={5}
    bg="orange.50"
    borderLeft="4px solid"
    borderColor="orange.500"
    borderRadius="sm"
    color="teal.900"
    fontSize="md"
  >
    {children}
  </Box>
);

// ---- Onglet Démarrer ----
const StartPanel = ({ visibleLink, hasLink }: { visibleLink: string; hasLink: boolean }) => {
  const steps: { titre: React.ReactNode; desc: React.ReactNode }[] = [
    {
      titre: 'Votre lien personnel est déjà prêt',
      desc: (
        <>
          Il est repris automatiquement de votre compte et injecté dans les exemples et les QR codes
          du kit — rien à saisir.
          {hasLink ? (
            <Box
              mt={2}
              px={3}
              py={2}
              bg="gray.50"
              borderRadius="sm"
              fontFamily="mono"
              fontSize="sm"
              color="teal.900"
              isTruncated
              title={visibleLink}
            >
              {visibleLink}
            </Box>
          ) : (
            <Text mt={2} color="orange.600" fontSize="sm">
              Aucun code apporteur n’est associé à votre compte : contactez votre courtier pour
              l’obtenir.
            </Text>
          )}
        </>
      ),
    },
    {
      titre: 'Sélectionnez un visuel dans l’onglet « Vignettes & bannières »',
      desc: 'Choisissez un format adapté au canal utilisé (post, story, ou sous le lien d’une annonce), puis téléchargez-le. Le QR code y est déjà personnalisé avec votre lien.',
    },
    {
      titre: 'Sélectionnez une accroche et un CTA dans l’onglet « Accroches & CTA »',
      desc: 'Parcourez les accroches et choisissez celle qui correspond le mieux à votre message et à votre réseau.',
    },
    {
      titre: 'Copiez la publication',
      desc: 'Cliquez sur « Copier » sous l’accroche choisie : le texte et votre lien personnel sont copiés ensemble. Collez-les dans la zone de publication de votre réseau, puis ajoutez le visuel téléchargé à l’étape 2.',
    },
    {
      titre: 'Publiez !',
      desc: 'Vérifiez le rendu, taguez @monemprunt.com lorsque le réseau le permet, et publiez.',
    },
  ];

  return (
    <>
      <Panel p={6} mb={8}>
        <Kicker>Bienvenue dans votre kit</Kicker>
        <SectionTitle>Générez de nouveaux contacts grâce à votre lien personnalisé</SectionTitle>
      </Panel>

      <VStack align="stretch" spacing={4} mb={8}>
        {steps.map((step, i) => (
          <Panel key={i} p={6} {...cardHover}>
            <HStack align="flex-start" spacing={5}>
              <Flex
                flexShrink={0}
                w="44px"
                h="44px"
                borderRadius="full"
                bg="teal.100"
                color="teal.500"
                align="center"
                justify="center"
                fontSize="2xl"
                fontWeight="bold"
              >
                {i + 1}
              </Flex>
              <Box>
                <Text fontWeight="bold" color="teal.900" fontSize="lg">
                  {step.titre}
                </Text>
                <Box mt={2} fontSize="md" color="gray.500">
                  {step.desc}
                </Box>
              </Box>
            </HStack>
          </Panel>
        ))}
      </VStack>

      <Note>
        <b>Bon réflexe :</b> une publication = une seule promesse. Parlez soit de capacité
        d’emprunt, soit de mensualité, soit de préparation du projet.
      </Note>
    </>
  );
};

// ---- Onglet Annonces ----
const AnnouncesPanel = ({ visibleLink }: { visibleLink: string }) => (
  <>
    <Kicker>Exemple d’annonce immobilière</Kicker>
    <SectionTitle>Valorisez le bien. Ajoutez le financement avec finesse.</SectionTitle>
    <Lede>
      Le financement doit soutenir la décision du prospect, sans masquer les photos ou détourner
      l’attention du bien.
    </Lede>

    <Grid templateColumns={{ base: '1fr', md: '.8fr 1.2fr' }} gap={7} mt={7} alignItems="start">
      <Image
        src={`${ASSETS}/example_announcement.png`}
        alt="Exemple d'annonce avec intégration Monemprunt"
        w="100%"
        borderRadius="md"
        boxShadow="xs"
      />
      <Box>
        <Panel p={6}>
          <Heading as="h3" fontSize="3xl" color="teal.900" mb={4}>
            La bonne utilisation
          </Heading>
          <VStack align="stretch" spacing={4}>
            {[
              [
                'Sous le lien de l’annonce',
                'Ajoutez une bannière horizontale avec l’angle “capacité d’emprunt ou mensualité pour ce bien”.',
              ],
              [
                'Sur la première photo uniquement',
                'Placez une mini-bannière en bas de l’image : discrète, maximum 12 à 15 % de la hauteur.',
              ],
              [
                'Préservez la lisibilité du bien',
                'Ne posez pas de bannière sur les autres photos du carrousel.',
              ],
              [
                'Utilisez votre lien personnel',
                'Il est déjà repris de votre compte pour générer les bannières avec QR code.',
              ],
            ].map(([titre, desc]) => (
              <HStack
                key={titre}
                align="flex-start"
                spacing={3}
                p={5}
                border="1px solid"
                borderColor="gray.100"
                borderRadius="md"
              >
                <Flex
                  bg="green.500"
                  color="white"
                  borderRadius="full"
                  w="22px"
                  h="22px"
                  align="center"
                  justify="center"
                  fontWeight="bold"
                  flexShrink={0}
                  fontSize="sm"
                >
                  ✓
                </Flex>
                <Box>
                  <Text fontSize="md" fontWeight="bold" color="teal.900">
                    {titre}
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={3}>
                    {desc}
                  </Text>
                </Box>
              </HStack>
            ))}
          </VStack>
        </Panel>
        <Note>
          <b>Texte recommandé :</b>
          <br />« Découvrez votre capacité d’emprunt ou la mensualité pour ce bien. »
          <br />
          <Text as="span" color="teal.500" fontWeight="bold">
            {visibleLink}
          </Text>
        </Note>
      </Box>
    </Grid>
  </>
);

// ---- Onglet Vignettes & bannières ----
// V14 : chaque bannière est un template pré-designé (SVG/PNG) sur lequel on superpose
// uniquement le QR code (fond blanc arrondi) dans l'emplacement réservé.
type QrRect = { x: number; y: number; s: number };

type BannerSpec = {
  id: string;
  template: string;
  w: number;
  h: number;
  qr: QrRect | null;
  titre: string;
  desc: string;
  meta: string[];
  file: string;
};

const BANNER_SPECS: BannerSpec[] = [
  {
    id: 'bannerLarge',
    template: 'banner_large_template.svg',
    w: 1556,
    h: 553,
    qr: { x: 1265, y: 40, s: 210 },
    titre: 'Bannière QR — financement maîtrisé',
    desc: 'Format large et premium pour un post conseil LinkedIn/Facebook. Le QR code se place automatiquement dans l’emplacement prévu.',
    meta: ['Format paysage', 'Post conseil', 'QR personnalisé'],
    file: 'banniere-financement-maitrise-qr.png',
  },
  {
    id: 'bannerWideNew',
    template: 'banner_wide_template.svg',
    w: 1620,
    h: 264,
    qr: { x: 1134, y: 36, s: 188 },
    titre: 'Bannière QR — sous le lien de l’annonce',
    desc: 'À intégrer juste sous le lien de l’annonce pour aider le prospect à vérifier sa capacité d’emprunt avant de se positionner.',
    meta: ['Format horizontal', 'Sous le lien', 'QR personnalisé'],
    file: 'banniere-capacite-emprunt-bien-qr.png',
  },
  {
    id: 'bannerQrSmall',
    template: 'banner_qr_template.png',
    w: 1106,
    h: 356,
    qr: { x: 812, y: 54, s: 246 },
    titre: 'Vignette QR — financement maîtrisé',
    desc: 'Format compact pour publication dédiée, story ou visuel digital. Idéal pour pousser directement vers la simulation.',
    meta: ['Format compact', 'Story / post', 'QR personnalisé'],
    file: 'vignette-qr-financement-maitrise.png',
  },
  {
    id: 'bannerPartnerNew',
    template: 'banner_partner_template.svg',
    w: 260,
    h: 92,
    qr: null,
    titre: 'Vignette partenaire — tag discret',
    desc: 'À positionner en bas d’un visuel conseil. Ajoutez le tag @monemprunt.com dans la légende lorsque le réseau le permet.',
    meta: ['Sans QR', 'Partenariat', 'Très discret'],
    file: 'vignette-partenaire-monemprunt.png',
  },
  {
    id: 'bannerUnderLinkNew',
    template: 'banner_underlink_template.svg',
    w: 1164,
    h: 144,
    qr: null,
    titre: 'Bannière simple — sous le lien',
    desc: 'Version sans QR, à utiliser lorsque le lien personnalisé est déjà visible dans le texte de la publication.',
    meta: ['Sans QR', 'Format léger', 'Sous le lien'],
    file: 'banniere-simple-sous-lien.png',
  },
];

const BannersPanel = ({
  currentLink,
  notify,
}: {
  currentLink: string;
  notify: (m: string) => void;
}) => {
  const refs = useRef<Record<string, HTMLCanvasElement | null>>({});

  useEffect(() => {
    let cancelled = false;

    async function generate() {
      let qrSrc = '';
      try {
        qrSrc = await QRCode.toDataURL(currentLink || NEUTRAL_URL, {
          width: 240,
          margin: 1,
          color: { dark: C_DARK, light: '#ffffff' },
        });
      } catch (e) {
        qrSrc = '';
      }
      const qr = qrSrc ? await imageLoad(qrSrc) : null;

      // Chargement en parallèle des templates puis dessin.
      await Promise.all(
        BANNER_SPECS.map(async (s) => {
          const template = await imageLoad(`${ASSETS}/${s.template}`);
          if (cancelled) return;
          const c = refs.current[s.id];
          if (!c || !template) return;
          const ctx = c.getContext('2d');
          if (!ctx) return;

          // On préserve les coins transparents des templates : pas de fond blanc.
          ctx.clearRect(0, 0, s.w, s.h);
          ctx.drawImage(template, 0, 0, s.w, s.h);

          if (qr && s.qr) {
            // Fond blanc arrondi : masque le placeholder et garantit un QR lisible.
            ctx.fillStyle = '#ffffff';
            drawRounded(ctx, s.qr.x - 8, s.qr.y - 8, s.qr.s + 16, s.qr.s + 16, 14);
            ctx.fill();
            ctx.drawImage(qr, s.qr.x, s.qr.y, s.qr.s, s.qr.s);
          }
        }),
      );
    }

    generate();
    return () => {
      cancelled = true;
    };
  }, [currentLink]);

  const download = (id: string, name: string) => {
    const c = refs.current[id];
    if (!c) return;
    const a = document.createElement('a');
    a.download = name;
    a.href = c.toDataURL('image/png');
    a.click();
    notify('Bannière téléchargée');
  };

  return (
    <>
      <Kicker>Vignettes &amp; bannières</Kicker>
      <SectionTitle>
        Choisissez votre support. Votre QR code se met à jour automatiquement.
      </SectionTitle>
      <Lede>
        Votre lien personnel génère un QR code propre : les trois premiers modèles l’affichent dans
        l’emplacement prévu. Téléchargez le support qui vous convient.
      </Lede>

      <Note>
        <b>À savoir :</b> utilisez les formats horizontaux sous le lien d’une annonce, les vignettes
        QR pour un post ou une story, et la vignette partenaire pour un marquage discret. Testez
        toujours le QR avant publication.
      </Note>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mt={7}>
        {BANNER_SPECS.map((s) => (
          <Panel key={s.id} overflow="hidden" {...cardHover}>
            <Box bg="gray.50">
              <canvas
                ref={(el) => {
                  refs.current[s.id] = el;
                }}
                width={s.w}
                height={s.h}
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />
            </Box>
            <Box p={5}>
              <Text fontSize="md" fontWeight="bold" color="teal.900">
                {s.titre}
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2} mb={3}>
                {s.desc}
              </Text>
              <HStack spacing={2} flexWrap="wrap" mb={4}>
                {s.meta.map((m) => (
                  <Text
                    key={m}
                    fontSize="xs"
                    fontWeight="bold"
                    color="teal.500"
                    bg="teal.100"
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    {m}
                  </Text>
                ))}
              </HStack>
              <Button
                w="100%"
                size="sm"
                h={9}
                borderRadius="sm"
                bg="teal.500"
                color="white"
                _hover={{ bg: 'teal.900' }}
                onClick={() => download(s.id, s.file)}
              >
                Télécharger en PNG
              </Button>
            </Box>
          </Panel>
        ))}
      </SimpleGrid>
    </>
  );
};

// ---- Onglet Accroches & CTA ----
const CtaPanel = ({
  visibleLink,
  copy,
}: {
  visibleLink: string;
  copy: (text: string, msg: string) => void;
}) => (
  <>
    <Kicker>Accroches &amp; appels à l’action</Kicker>
    <SectionTitle>Des phrases simples pour expliquer l’intérêt du financement.</SectionTitle>

    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={7}>
      {CTAS.map(([cat, txt], i) => (
        <Panel key={`${cat}-${i}`} p={6} {...cardHover}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="1.2px"
            color="orange.500"
            textTransform="uppercase"
          >
            {cat}
          </Text>
          <Text fontSize="md" color="teal.900" my={2}>
            {txt}
          </Text>
          <Text fontSize="sm" color="teal.500" fontWeight="bold" mb={3} wordBreak="break-all">
            {visibleLink}
          </Text>
          <Button
            size="sm"
            h={9}
            borderRadius="sm"
            bg="teal.500"
            color="white"
            _hover={{ bg: 'teal.900' }}
            onClick={() => copy(`${txt}\n${visibleLink}`, 'Accroche copiée')}
          >
            Copier
          </Button>
        </Panel>
      ))}
    </SimpleGrid>
  </>
);

export const KitCommunicationPageRoute = () => {
  const me = useContext(MeContext);

  return (
    <Layout
      courtier={me.courtier!}
      utilisateur={me.utilisateur!}
      sidebar={<HomeSidebar withKPI={false} withFilter={false} utilisateur={me.utilisateur!} />}
      leftSidebar={true}
    >
      <KitCommunicationPage />
    </Layout>
  );
};
